import React, { useState, useEffect, useMemo } from 'react';
import toast from "react-hot-toast";
import AdvancedFormulaManager from './AdvancedFormulaManager.jsx';
import formulaEngine from './utils/FormulaEngine';
import SalaryStructureCalculationResults from './SalaryStructureCalculationResults.jsx';
import SalaryStructureApplicabilityStep from './SalaryStructureApplicabilityStep.jsx';
import {
  roundAmount,
  computePfEmployeeDeduction,
  computeEmployerStatutory,
  buildEmployerPfContributionRows,
  computeSalaryStructureSimulation,
  isEmployerShareComponent,
  isBasicLike,
  isRecurrentComponent,
  resolvePfWageBase,
  resolveGratuityWageBase,
} from './utils/salaryStructureSimulatorCompute';
import {
  branchService,
  categoryService,
  companyService,
  departmentService,
  designationService,
  gradesService,
  payrollOpsService,
  salaryStructureService,
  subDepartmentService,
} from '../../../../services/api';
import {
  buildSimulatorStatutoryConfig,
  computeEsiAmounts,
  computePtAmount,
  loadStatutoryRatesFromStorage,
  STATUTORY_RATES_CHANGED_EVENT,
  resolveStatutoryRatesForEffectiveDate,
  effectiveDateFromMonth,
} from './utils/statutoryRatesDefaults';

import {
  rematchStructureComponentsToLibrary,
  normalizeCode,
} from "./utils/salaryComponentLibrarySync";
import { DsButton, DsInput } from "../../../../design-system";
import { Info } from "lucide-react";
import {
  METHOD_FIXED,
  METHOD_MONTHLY_VARIABLE,
  PB_FREQUENCIES,
  normalizeStructureComponent,
  normalizeStructureComponents,
  isManualPercentComponent,
  isMonthlyVariableComponent,
  normalizeComponentNature,
  isNonPayrollComponent,
} from "../../../../utils/salaryStructureComponentRules";
import {
  defaultNpsConfig,
  defaultVpfConfig,
  defaultMealVoucherConfig,
  normalizeNpsConfig,
  normalizeVpfConfig,
  normalizeMealVoucherConfig,
  normalizePerformanceBonusConfig,
} from "../../../../utils/salaryStructureAddons";
import StructureAddonConfigModal from "./StructureAddonConfigModal";
import ComponentConfigHistoryModal from "./ComponentConfigHistoryModal";
import {
  exportStructureComponentConfigHistory,
  consolidateComponentHistory,
} from "./utils/exportStructureComponentConfigHistory";
import {
  resolveCompanyMinMonthValue,
  clampYm,
} from "../../../../utils/selectableDateBounds";

const EMPTY_ORG_LEVELS = {
  branches: [],
  departments: [],
  designations: [],
  grades: [],
  categories: [],
  subDepartments: [],
};

function nextMonthLabel(monthLabel = "") {
  const m = String(monthLabel || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return "";
  let year = Number(m[1]);
  let month = Number(m[2]) + 1;
  if (month > 12) {
    month = 1;
    year += 1;
  }
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** Normalize library component category (string or object) into structure form shape. */
function normalizeCategories(component = {}) {
  if (component.categories && typeof component.categories === 'object') {
    return {
      earning: !!component.categories.earning,
      deduction: !!component.categories.deduction,
      benefit: !!component.categories.benefit,
      reimbursement: !!component.categories.reimbursement,
    };
  }
  const cat = String(component.category || '').toUpperCase();
  return {
    earning: cat === 'EARNING',
    deduction: cat === 'DEDUCTION',
    benefit: cat === 'BENEFIT' || cat === 'EMPLOYER_BENEFIT',
    reimbursement: cat === 'REIMBURSEMENT',
  };
}

function hasCategory(component, key) {
  if (component?.categories && typeof component.categories === "object") {
    return !!component.categories[key];
  }
  const raw = String(component?.category || "").toUpperCase();
  if (key === "earning") return raw === "EARNING";
  if (key === "deduction") return raw === "DEDUCTION";
  if (key === "benefit") return raw === "BENEFIT" || raw === "EMPLOYER_BENEFIT";
  if (key === "reimbursement") return raw === "REIMBURSEMENT";
  return false;
}

function buildStructureComponent(component, existingComponents = []) {
  const categories = normalizeCategories(component);
  const basic = isBasicLike(component);
  const usedOrders = new Set(
    (existingComponents || []).map((c) => Number(c.orderNo)).filter((n) => Number.isFinite(n))
  );
  let orderNo = Number(component.orderNo ?? component.sortOrder);
  if (!Number.isFinite(orderNo) || orderNo <= 0) {
    if (basic) {
      orderNo = 1;
    } else {
      orderNo = 2;
      while (usedOrders.has(orderNo)) orderNo += 1;
    }
  }
  if (basic) orderNo = 1;
  return {
    id: component.id,
    name: component.name,
    code: component.code,
    categories,
    cumulative: component.cumulative || 'no',
    calculationType: 'manual',
    calculationMethod: METHOD_FIXED,
    calculationBasis: 'payDays',
    manualAmount: '',
    manualPercent: '',
    manualAmountType: 'amount',
    percentBasis: 'CTC',
    // Basic defaults to PF + Gratuity applicable so statutory toggles work out of the box
    pfApplicable: basic,
    esiApplicable: false,
    gratuityApplicable: basic,
    // Reimbursement: tax/nature/method come from library + TDS head; structure only needs ceiling.
    taxTreatment: categories.reimbursement ? 'partial' : 'taxable',
    selectedFormula: null,
    formulaResult: null,
    // Basic is never a CTC balancer; reimbursements never balance CTC
    autoAdjustCTC: basic || categories.reimbursement ? false : !!component.autoAdjustCTC,
    isActive: true,
    statusEffectiveFrom: '',
    componentNature: 'payroll',
    orderNo,
  };
}

const SalaryStructureForm = ({ isOpen, onClose, onSubmit, savedComponents, loading, editingStructure, organizationalLevels = {}, statutoryRates = {}, statutoryConfigJson = null, structures = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    salaryType: 'ctc',
    components: [],
    currentStep: 1,
    effectiveMonth: '',
    performanceBonus: normalizePerformanceBonusConfig({ enabled: false, frequency: 'annual' }),
    nps: defaultNpsConfig(),
    vpf: defaultVpfConfig(),
    mealVoucher: defaultMealVoucherConfig(),
    assignment: {
      allEmployees: false,
      branch: '',
      department: '',
      designation: '',
      grade: '',
      category: '',
      subDepartment: '',
      location: '',
      employmentTypes: [],
      employeeIds: [],
    }
  });

  const [showFormulaManager, setShowFormulaManager] = useState(false);
  const [currentComponentForFormula, setCurrentComponentForFormula] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [simulatorValidated, setSimulatorValidated] = useState(false);
  /** Opt-in Step 3; only meaningful when at least one component uses Formula. */
  const [enableSimulator, setEnableSimulator] = useState(false);
  const [simulatorCtcAmount, setSimulatorCtcAmount] = useState('');
  const [simulatorCtcType, setSimulatorCtcType] = useState('monthly'); // 'monthly' | 'annual'
  const [simulatorPbAmount, setSimulatorPbAmount] = useState('');
  const [simulatorPbPeriod, setSimulatorPbPeriod] = useState('annual'); // 'monthly' | 'annual'
  const [historyModalComponentId, setHistoryModalComponentId] = useState(null);
  const [savingComponentId, setSavingComponentId] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [exportingConfig, setExportingConfig] = useState(false);
  const [addonModal, setAddonModal] = useState(null); // performanceBonus | nps | vpf | mealVoucher
  const [addonInfoKey, setAddonInfoKey] = useState(null);
  const [simulatorPtState, setSimulatorPtState] = useState(() => {
    const rates =
      statutoryRates && Object.keys(statutoryRates).length
        ? statutoryRates
        : loadStatutoryRatesFromStorage();
    return rates?.pt?.currentState || 'Karnataka';
  });
  const [simulatorApplicability, setSimulatorApplicability] = useState({
    pfApplicable: true,
    esiApplicable: true,
    ptApplicable: true,
    gratuityApplicable: true,
    ceilingPf: true // Match employee Ceiling PF default (statutory wage ceiling)
  });
  const [followMinimumWages, setFollowMinimumWages] = useState(false);
  const [simulatorCategories, setSimulatorCategories] = useState([]);
  const [simulatorCategoryId, setSimulatorCategoryId] = useState('');
  const [loadedOrgLevels, setLoadedOrgLevels] = useState(EMPTY_ORG_LEVELS);
  const [latestFinalizedPayrollMonth, setLatestFinalizedPayrollMonth] = useState("");
  const [minimumAllowedEffectiveMonth, setMinimumAllowedEffectiveMonth] = useState("");
  const companyMinMonth = useMemo(() => resolveCompanyMinMonthValue() || "", []);
  const statusFromMinMonth = useMemo(() => {
    if (companyMinMonth && minimumAllowedEffectiveMonth) {
      return companyMinMonth > minimumAllowedEffectiveMonth
        ? companyMinMonth
        : minimumAllowedEffectiveMonth;
    }
    return minimumAllowedEffectiveMonth || companyMinMonth || undefined;
  }, [companyMinMonth, minimumAllowedEffectiveMonth]);
  const [simulatorMwMonth, setSimulatorMwMonth] = useState(
    () => new Date().toISOString().slice(0, 7)
  );
  const [simulatorMwAmount, setSimulatorMwAmount] = useState(null);
  const [liveStatutoryRates, setLiveStatutoryRates] = useState(() =>
    statutoryRates && Object.keys(statutoryRates).length
      ? statutoryRates
      : loadStatutoryRatesFromStorage()
  );

  useEffect(() => {
    if (statutoryRates && Object.keys(statutoryRates).length) {
      setLiveStatutoryRates(statutoryRates);
    }
  }, [statutoryRates]);

  useEffect(() => {
    if (!statutoryConfigJson) return;
    const effectiveKey = effectiveDateFromMonth(formData.effectiveMonth);
    setLiveStatutoryRates(resolveStatutoryRatesForEffectiveDate(statutoryConfigJson, effectiveKey));
  }, [statutoryConfigJson, formData.effectiveMonth]);

  useEffect(() => {
    const onRates = (e) => {
      if (e?.detail) setLiveStatutoryRates(e.detail);
      else setLiveStatutoryRates(loadStatutoryRatesFromStorage());
    };
    window.addEventListener(STATUTORY_RATES_CHANGED_EVENT, onRates);
    return () => window.removeEventListener(STATUTORY_RATES_CHANGED_EVENT, onRates);
  }, []);

  // Load org MW flag + categories for simulator when form opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      try {
        const rawUser = localStorage.getItem('user');
        const rawCompany = localStorage.getItem('companyData');
        const companyId = Number(
          (rawUser ? JSON.parse(rawUser)?.companyId : null) ||
            (rawCompany ? JSON.parse(rawCompany)?.id : null) ||
            0
        );
        if (companyId) {
          const mwRes = await companyService.getMinimumWagesSetting(companyId);
          if (!cancelled) {
            setFollowMinimumWages(!!mwRes?.data?.followMinimumWages);
          }
        }
        const catRes = await categoryService.getAllCategories();
        if (!cancelled && catRes?.success) {
          setSimulatorCategories(catRes.data || []);
        }
      } catch {
        if (!cancelled) setFollowMinimumWages(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const loadFinalizedPayrollCutoff = async () => {
      try {
        const res = await payrollOpsService.listFinalizedPayrollMonths();
        const list = Array.isArray(res?.data) ? res.data : [];
        const latest = list
          .map((row) => String(row?.payMonth || "").slice(0, 7))
          .filter((m) => /^\d{4}-\d{2}$/.test(m))
          .sort()
          .pop() || "";
        if (cancelled) return;
        setLatestFinalizedPayrollMonth(latest);
        setMinimumAllowedEffectiveMonth(latest ? nextMonthLabel(latest) : "");
      } catch {
        if (cancelled) return;
        setLatestFinalizedPayrollMonth("");
        setMinimumAllowedEffectiveMonth("");
      }
    };
    loadFinalizedPayrollCutoff();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Fallback: load Masters (same APIs as Masters.jsx) when parent did not pass lists
  useEffect(() => {
    if (!isOpen) return;
    const propBranchCount = organizationalLevels.branches?.length || 0;
    const propDeptCount = organizationalLevels.departments?.length || 0;
    const propDesigCount = organizationalLevels.designations?.length || 0;
    const propGradeCount = organizationalLevels.grades?.length || 0;
    const propCatCount = organizationalLevels.categories?.length || 0;
    const propSubCount = organizationalLevels.subDepartments?.length || 0;
    if (
      propBranchCount +
        propDeptCount +
        propDesigCount +
        propGradeCount +
        propCatCount +
        propSubCount >
      0
    ) {
      return;
    }

    let cancelled = false;
    const loadMasters = async () => {
      try {
        const [
          branchesRes,
          departmentsRes,
          designationsRes,
          gradesRes,
          categoriesRes,
          subDepartmentsRes,
        ] = await Promise.all([
          branchService.getBranches(),
          departmentService.getDepartments(),
          designationService.getDesignations(),
          gradesService.getGrades(),
          categoryService.getAllCategories(),
          subDepartmentService.getAllSubDepartments(),
        ]);
        if (cancelled) return;
        setLoadedOrgLevels({
          branches: branchesRes?.success ? branchesRes.data || [] : [],
          departments: departmentsRes?.success ? departmentsRes.data || [] : [],
          designations: designationsRes?.success ? designationsRes.data || [] : [],
          grades: gradesRes?.success ? gradesRes.data || [] : [],
          categories: categoriesRes?.success ? categoriesRes.data || [] : [],
          subDepartments: subDepartmentsRes?.success
            ? subDepartmentsRes.data || []
            : [],
        });
      } catch (err) {
        console.error('Failed to load masters for salary structure applicability', err);
      }
    };
    loadMasters();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when open or prop list lengths change
  }, [
    isOpen,
    organizationalLevels.branches?.length,
    organizationalLevels.departments?.length,
    organizationalLevels.designations?.length,
    organizationalLevels.grades?.length,
    organizationalLevels.categories?.length,
    organizationalLevels.subDepartments?.length,
  ]);

  const resolvedOrgLevels = {
    branches: organizationalLevels.branches?.length
      ? organizationalLevels.branches
      : loadedOrgLevels.branches,
    departments: organizationalLevels.departments?.length
      ? organizationalLevels.departments
      : loadedOrgLevels.departments,
    designations: organizationalLevels.designations?.length
      ? organizationalLevels.designations
      : loadedOrgLevels.designations,
    grades: organizationalLevels.grades?.length
      ? organizationalLevels.grades
      : loadedOrgLevels.grades,
    categories: organizationalLevels.categories?.length
      ? organizationalLevels.categories
      : loadedOrgLevels.categories,
    subDepartments: organizationalLevels.subDepartments?.length
      ? organizationalLevels.subDepartments
      : loadedOrgLevels.subDepartments,
  };

  // Resolve MW amount for selected category + month
  useEffect(() => {
    if (!followMinimumWages || !simulatorCategoryId || !simulatorMwMonth) {
      setSimulatorMwAmount(null);
      return;
    }
    let cancelled = false;
    const resolve = async () => {
      try {
        const res = await categoryService.resolveMinimumWage(
          simulatorCategoryId,
          simulatorMwMonth,
          formData?.assignment?.branch || null
        );
        if (!cancelled) {
          setSimulatorMwAmount(
            res?.data?.amount != null ? Number(res.data.amount) : null
          );
        }
      } catch {
        if (!cancelled) setSimulatorMwAmount(null);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [followMinimumWages, simulatorCategoryId, simulatorMwMonth, formData?.assignment?.branch]);

  // Reset form when modal opens or when editing structure changes
  useEffect(() => {
    if (isOpen) {
      if (editingStructure) {
        const rematched = rematchStructureComponentsToLibrary(
          editingStructure.components || [],
          savedComponents || []
        );
        setFormData({
          name: editingStructure.name,
          salaryType: editingStructure.salaryType || 'ctc',
          components: normalizeStructureComponents(
            rematched.map((c, idx) => {
              const basic = isBasicLike(c);
              const orderNo = basic
                ? 1
                : Number.isFinite(Number(c.orderNo ?? c.sortOrder))
                  ? Number(c.orderNo ?? c.sortOrder)
                  : idx + 1;
              return {
                ...c,
                categories: normalizeCategories(c),
                autoAdjustCTC: basic ? false : !!c.autoAdjustCTC,
                orderNo,
              };
            })
          ),
          currentStep: 1,
          effectiveMonth: editingStructure.effectiveMonth || '',
          performanceBonus: normalizePerformanceBonusConfig(editingStructure.performanceBonus),
          nps: normalizeNpsConfig(editingStructure.nps),
          vpf: normalizeVpfConfig(editingStructure.vpf),
          mealVoucher: normalizeMealVoucherConfig(editingStructure.mealVoucher),
          assignment: editingStructure.assignment || {
            allEmployees: false,
            branch: '',
            department: '',
            designation: '',
            grade: '',
            category: '',
            subDepartment: '',
            location: '',
            employmentTypes: [],
            employeeIds: [],
          }
        });
      } else {
        setFormData({
          name: '',
          salaryType: 'ctc',
          components: [],
          currentStep: 1,
          effectiveMonth: '',
          performanceBonus: normalizePerformanceBonusConfig({ enabled: false, frequency: 'annual' }),
          nps: defaultNpsConfig(),
          vpf: defaultVpfConfig(),
          mealVoucher: defaultMealVoucherConfig(),
          assignment: {
            allEmployees: false,
            branch: '',
            department: '',
            designation: '',
            grade: '',
            category: '',
            subDepartment: '',
            location: '',
            employmentTypes: [],
            employeeIds: [],
          }
        });
      }
      setSimulatorValidated(false);
      setEnableSimulator(false);
      setSimulatorCtcAmount("");
    }
  }, [isOpen, editingStructure]);

  const hasFormulaComponent = useMemo(
    () =>
      (formData.components || []).some(
        (c) => String(c?.calculationType || "").toLowerCase() === "formula"
      ),
    [formData.components]
  );

  // Simulator opt-in is only available when a Formula component exists.
  useEffect(() => {
    if (!hasFormulaComponent && enableSimulator) {
      setEnableSimulator(false);
    }
  }, [hasFormulaComponent, enableSimulator]);

  // When library sync adds/updates components, rematch selected rows onto library ids
  useEffect(() => {
    if (!isOpen || !(savedComponents || []).length) return;
    setFormData((prev) => {
      if (!prev.components?.length) return prev;
      const rematched = rematchStructureComponentsToLibrary(
        prev.components,
        savedComponents
      );
      const changed = rematched.some(
        (c, i) =>
          String(c.id) !== String(prev.components[i]?.id) ||
          String(c.name || "") !== String(prev.components[i]?.name || "") ||
          String(c.code || "") !== String(prev.components[i]?.code || "")
      );
      if (!changed) return prev;
      return {
        ...prev,
        components: rematched.map((c) => ({
          ...c,
          categories: normalizeCategories(c),
        })),
      };
    });
  }, [isOpen, savedComponents]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "effectiveMonth" && minimumAllowedEffectiveMonth && value && value < minimumAllowedEffectiveMonth) {
      setDuplicateWarning({
        type: "effective-month-retrospective",
        message:
          `Payroll is finalised up to ${latestFinalizedPayrollMonth}. First unlock finalised payroll month(s), then choose effective month ${minimumAllowedEffectiveMonth} or later. Warning: changing this may alter payroll values against actual payments.`,
      });
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Check for effective month warnings
    if (name === 'effectiveMonth' && editingStructure) {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const originalMonth = editingStructure.effectiveMonth;
      
      if (value === originalMonth) {
        setDuplicateWarning({
          type: 'effective-month-same',
          message: 'You are selecting the same effective month. This will not change the effective date but may affect salary processing.'
        });
      } else if (value < currentMonth) {
        setDuplicateWarning({
          type: 'effective-month-retrospective',
          message: 'Warning: Selecting a retrospective effective month will lead to processed salary changes from the selected month.'
        });
      } else {
        setDuplicateWarning(null);
      }
    }

    // Check for duplicate structure names and assignments
    if (name === 'name' || name === 'effectiveMonth') {
      checkForDuplicates();
    }
  };

  const checkForDuplicates = () => {
    // This would typically check against existing structures
    // For now, we'll simulate the check
    const existingStructures = [
      { name: 'Manager Structure', effectiveMonth: '2024-01', assignment: { department: ['hr'], designation: ['manager'] } },
      { name: 'Executive Structure', effectiveMonth: '2024-02', assignment: { department: ['it'], designation: ['executive'] } }
    ];

    const currentAssignment = JSON.stringify(formData.assignment);
    
    const duplicateName = existingStructures.find(structure => 
      structure.name.toLowerCase() === formData.name.toLowerCase() && 
      structure.effectiveMonth === formData.effectiveMonth &&
      editingStructure?.id !== structure.id
    );

    const duplicateAssignment = existingStructures.find(structure => 
      JSON.stringify(structure.assignment) === currentAssignment &&
      editingStructure?.id !== structure.id
    );

    if (duplicateName) {
      setDuplicateWarning({
        type: 'duplicate-name',
        message: `A salary structure with the same name "${formData.name}" and effective month "${formData.effectiveMonth}" already exists.`
      });
    } else if (duplicateAssignment) {
      setDuplicateWarning({
        type: 'duplicate-assignment',
        message: 'The selected organizational levels and employees are already assigned to another salary structure.'
      });
    } else {
      setDuplicateWarning(null);
    }
  };

  const isComponentSelected = (libraryComp, selectedList = formData.components) => {
    const libCode = normalizeCode(libraryComp?.code, libraryComp?.name);
    return (selectedList || []).some(
      (comp) =>
        String(comp.id) === String(libraryComp.id) ||
        (libCode && normalizeCode(comp.code, comp.name) === libCode)
    );
  };

  const handleComponentAdd = (componentId) => {
    const component = savedComponents.find((comp) => String(comp.id) === String(componentId));
    if (component && !isComponentSelected(component)) {
      setFormData((prev) => ({
        ...prev,
        components: [...prev.components, buildStructureComponent(component, prev.components)],
      }));
    }
  };

  const handleComponentRemove = (componentId) => {
    const libraryComp = savedComponents.find((c) => String(c.id) === String(componentId));
    const code = normalizeCode(libraryComp?.code, libraryComp?.name);
    setFormData((prev) => ({
      ...prev,
      components: prev.components.filter(
        (comp) =>
          String(comp.id) !== String(componentId) &&
          !(code && normalizeCode(comp.code, comp.name) === code)
      ),
    }));
  };

  const handleComponentSettingChange = (componentId, setting, value) => {
    setFormData((prev) => ({
      ...prev,
      components: prev.components.map((comp) => {
        if (comp.id !== componentId) return comp;
        let updatedComp = { ...comp, [setting]: value };

        if (setting === "manualAmountType" && value === "amount") {
          updatedComp.manualAmountType = "amount";
        }
        if (setting === "manualAmountType" && value === "percent") {
          updatedComp.manualAmountType = "percent";
        }
        if (setting === "calculationMethod" && value === METHOD_MONTHLY_VARIABLE) {
          updatedComp.autoAdjustCTC = false;
          updatedComp.manualAmountType = "amount";
        }
        if (setting === "calculationMethod" && value === METHOD_FIXED) {
          if (
            String(updatedComp.calculationBasis || "").toLowerCase() === "recurrent"
          ) {
            updatedComp.calculationBasis = "payDays";
          }
        }
        if (setting === "isActive" && value === false && !updatedComp.statusEffectiveFrom) {
          updatedComp.statusEffectiveFrom =
            minimumAllowedEffectiveMonth ||
            new Date().toISOString().slice(0, 7);
        }
        if (setting === "isActive" && value === true && !updatedComp.statusEffectiveFrom) {
          updatedComp.statusEffectiveFrom =
            minimumAllowedEffectiveMonth ||
            new Date().toISOString().slice(0, 7);
        }
        if (setting === "componentNature") {
          updatedComp.componentNature = normalizeComponentNature(value);
        }
        // Basic can never be a CTC balancer
        if (isBasicLike(updatedComp)) {
          updatedComp.autoAdjustCTC = false;
          if (setting === "orderNo") updatedComp.orderNo = 1;
          updatedComp.calculationMethod = METHOD_FIXED;
        }
        if (setting === "orderNo") {
          const n = parseInt(value, 10);
          updatedComp.orderNo = Number.isFinite(n) && n > 0 ? n : isBasicLike(updatedComp) ? 1 : 2;
        }

        return normalizeStructureComponent(updatedComp);
      }),
    }));
  };

  const applyComponentFromDb = (savedComponent) => {
    if (!savedComponent) return;
    setFormData((prev) => ({
      ...prev,
      components: prev.components.map((comp) =>
        String(comp.id) === String(savedComponent.id)
          ? normalizeStructureComponent({ ...comp, ...savedComponent })
          : comp
      ),
    }));
  };

  const handleSaveComponentConfig = async (componentId) => {
    const templateId = editingStructure?.id;
    if (!templateId) {
      toast.error("Save the salary structure once first, then use component Save for immediate DB versions.");
      return;
    }
    const component = formData.components.find((c) => String(c.id) === String(componentId));
    if (!component) return;
    const effYm = String(component.statusEffectiveFrom || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(effYm)) {
      toast.error("Set Status from (month) before saving component configuration");
      return;
    }
    setSavingComponentId(componentId);
    try {
      const res = await salaryStructureService.patchTemplateComponent(
        templateId,
        componentId,
        normalizeStructureComponent(component),
        "Saved configuration"
      );
      const saved = res?.data?.component;
      if (saved) applyComponentFromDb(saved);
      toast.success(`${component.name || "Component"} saved to database`);
      if (res?.reimbSync?.started) {
        toast("Employee structures syncing in the background…", { icon: "ℹ️" });
      } else if (
        res?.reimbSync?.reason === "FINALIZED_PAYROLL_MONTH_LOCK" ||
        res?.reimbSync?.reason === "finalized_month_lock" ||
        res?.reimbSync?.reason === "STRUCTURE_REVISION_MONTH_LOCKED"
      ) {
        toast.error(
          res.reimbSync.message ||
            "Cannot sync: Status from is in a finalised/locked payroll month. Change Status from, or unprocess/unfinalise that month first."
        );
      } else if (res?.reimbSync?.reason === "missing_status_from") {
        toast.error(res.reimbSync.message || "Set Status from on reimbursement components.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to save component");
    } finally {
      setSavingComponentId(null);
    }
  };

  const handleRestoreComponentHistory = async (componentId, historyId) => {
    const templateId = editingStructure?.id;
    const ok = window.confirm(
      "Restore this saved configuration from the database?\n\n" +
        "Future payroll and structure apply will use the restored settings. " +
        "Already processed payroll keeps historical amounts.\n\nContinue?"
    );
    if (!ok) return;
    if (!templateId) {
      toast.error("Structure must exist in DB to restore history.");
      return;
    }
    setHistoryBusy(true);
    try {
      const res = await salaryStructureService.restoreTemplateComponentHistory(
        templateId,
        componentId,
        historyId
      );
      const saved = res?.data?.component;
      if (saved) applyComponentFromDb(saved);
      toast.success("Component configuration restored from database");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Restore failed");
    } finally {
      setHistoryBusy(false);
    }
  };

  const handleDeleteComponentHistory = async (componentId, historyId) => {
    const templateId = editingStructure?.id;
    const ok = window.confirm(
      "Delete this version and restore the previous saved configuration into the live component?\n\nThis writes to the database immediately."
    );
    if (!ok) return;
    if (!templateId) {
      toast.error("Structure must exist in DB to delete history.");
      return;
    }
    setHistoryBusy(true);
    try {
      const res = await salaryStructureService.deleteTemplateComponentHistory(
        templateId,
        componentId,
        historyId
      );
      const saved = res?.data?.component;
      if (saved) applyComponentFromDb(saved);
      toast.success("History version deleted; previous configuration restored");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Delete failed");
    } finally {
      setHistoryBusy(false);
    }
  };

  const handleExportConfigHistory = async () => {
    setExportingConfig(true);
    try {
      const filename = await exportStructureComponentConfigHistory({
        structureName: formData.name || editingStructure?.name || "Salary Structure",
        components: formData.components,
      });
      toast.success(`Exported ${filename}`);
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExportingConfig(false);
    }
  };

  const handleFormulaSelect = (formula) => {
    if (currentComponentForFormula) {
      setFormData(prev => ({
        ...prev,
        components: prev.components.map(comp => 
          comp.id === currentComponentForFormula 
            ? { ...comp, selectedFormula: formula, formulaResult: null }
            : comp
        )
      }));
      setShowFormulaManager(false);
      setCurrentComponentForFormula(null);
    }
  };

  const openFormulaManager = (componentId) => {
    setCurrentComponentForFormula(componentId);
    setShowFormulaManager(true);
  };

  // Rates from StatutoryRatesManager — resolved for structure effective month when config JSON available
  const getStatutoryConfig = () => {
    const effectiveKey = effectiveDateFromMonth(formData.effectiveMonth);
    const rateSource = statutoryConfigJson
      ? resolveStatutoryRatesForEffectiveDate(statutoryConfigJson, effectiveKey)
      : liveStatutoryRates;
    return buildSimulatorStatutoryConfig(rateSource, simulatorPtState);
  };

  const estimateFixedCtcAddonReserve = (monthlyPackage) => {
    let reserve = 0;
    const pkg = Math.max(0, Number(monthlyPackage) || 0);
    const basicGuess = pkg * 0.4;
    if (formData.nps?.enabled) {
      const pct = Number(formData.nps.flatPercent) || 0;
      if (formData.nps.rateMode === "flat" && pct > 0) {
        reserve += roundAmount((basicGuess * pct) / 100);
      }
    }
    if (formData.mealVoucher?.enabled && formData.mealVoucher.rateMode === "flat") {
      reserve += roundAmount(Number(formData.mealVoucher.flatMonthlyAmount) || 0);
    }
    return roundAmount(reserve);
  };

  const getSimulatorContextAndAmounts = (ctcValue) => {
    const packageSplitPreview = (() => {
      try {
        const raw = Number(String(ctcValue || "").replace(/,/g, "")) || 50000;
        const annual =
          simulatorCtcType === "monthly" ? raw * 12 : raw;
        const pb = formData.performanceBonus?.enabled
          ? Number(simulatorPbAmount) || 0
          : 0;
        const pbAnnual =
          simulatorPbPeriod === "monthly" ? pb * 12 : pb;
        return Math.max(0, (annual - pbAnnual) / 12);
      } catch {
        return 50000;
      }
    })();
    return computeSalaryStructureSimulation({
      components: formData.components,
      salaryType: formData.salaryType,
      ctcValue,
      ctcPeriod: simulatorCtcType,
      statutoryConfig: getStatutoryConfig(),
      applicability: simulatorApplicability,
      formulaEngine,
      minimumWageAmount:
        followMinimumWages && simulatorMwAmount != null ? simulatorMwAmount : null,
      asOfDate: effectiveDateFromMonth(formData.effectiveMonth) || new Date().toISOString().slice(0, 10),
      performanceBonusAmount: formData.performanceBonus?.enabled
        ? Number(simulatorPbAmount) || 0
        : 0,
      performanceBonusPeriod: simulatorPbPeriod,
      fixedCtcReserveMonthly: estimateFixedCtcAddonReserve(packageSplitPreview),
    });
  };

  const getSimulatorTotals = () => {
    const sim = getSimulatorContextAndAmounts(simulatorCtcAmount);
    const { amountsByComponentId, monthlyCtc, config, balanceComponentId } = sim;

    let gross = 0;
    formData.components.forEach((c) => {
      if (isRecurrentComponent(c)) return;
      const amt = amountsByComponentId[c.id] || 0;
      if (hasCategory(c, "earning")) gross += amt;
      if (hasCategory(c, "benefit")) gross += amt;
      if (hasCategory(c, "reimbursement")) gross += amt;
    });

    const pfBase = resolvePfWageBase(
      formData.components,
      amountsByComponentId,
      simulatorApplicability
    );
    const gratuityBase = resolveGratuityWageBase(
      formData.components,
      amountsByComponentId,
      simulatorApplicability
    );
    const esiBase = gross;

    const statutory = computeEmployerStatutory(
      pfBase,
      gratuityBase,
      esiBase,
      config,
      simulatorApplicability
    );
    const esiEmployerAmount = statutory.esiEmployer;
    const gratuityAmount = statutory.gratuity;

    const pfEmployeeAmount = computePfEmployeeDeduction(
      pfBase,
      config,
      simulatorApplicability
    );

    const esiParts = computeEsiAmounts(
      esiBase,
      config.esi,
      simulatorApplicability.esiApplicable
    );
    const esiEmployeeAmount = esiParts.employee;
    // Prefer employer from computeEmployerStatutory (same rules); keep in sync if mismatch
    const esiEmployerFinal = esiParts.employer;

    const ptAmount = roundAmount(
      computePtAmount({
        monthlyGross: gross,
        ptStateConfig: config.ptStateConfig,
        ptSlabs: config.ptSlabs,
        month: new Date().getMonth() + 1,
        ptApplicable: simulatorApplicability.ptApplicable,
        stateName: config.ptState,
      })
    );

    const statutoryDeductionsList = [
      ...(simulatorApplicability.pfApplicable
        ? [{ name: "PF (Employee)", amount: pfEmployeeAmount }]
        : []),
      ...(simulatorApplicability.esiApplicable
        ? [{ name: "ESI (Employee)", amount: esiEmployeeAmount }]
        : []),
      ...(simulatorApplicability.ptApplicable
        ? [{ name: "Professional Tax (PT)", amount: ptAmount }]
        : []),
    ];
    const statutoryEmployerList = [
      ...buildEmployerPfContributionRows(
        statutory,
        simulatorApplicability.pfApplicable
      ),
      ...(simulatorApplicability.esiApplicable
        ? [{ name: "ESI (Employer)", amount: roundAmount(esiEmployerFinal || esiEmployerAmount) }]
        : []),
      ...(simulatorApplicability.gratuityApplicable
        ? [{ name: "Gratuity", amount: roundAmount(gratuityAmount) }]
        : []),
    ];

    const totals = {
      earnings: 0,
      deductions: 0,
      benefits: 0,
      reimbursements: 0,
      gross: 0,
      net: 0,
      employerShares: 0,
      nonPayroll: 0,
      addonFixedCtc: 0,
    };
    const earningsList = [];
    const deductionsList = [];
    const employerList = [];
    const benefitsList = [];
    const reimbursementsList = [];

    formData.components.forEach((component) => {
      if (isRecurrentComponent(component)) return;
      const amount = roundAmount(amountsByComponentId[component.id] || 0);
      const isEmployer = isEmployerShareComponent(component);
      const nonPayroll = isNonPayrollComponent(component);
      if (hasCategory(component, "earning")) {
        if (nonPayroll) {
          totals.nonPayroll = roundAmount((totals.nonPayroll || 0) + amount);
        } else {
          totals.earnings += amount;
          earningsList.push({ name: component.name, amount });
        }
      } else if (hasCategory(component, "deduction")) {
        if (isEmployer) {
          totals.employerShares += amount;
          employerList.push({ name: component.name, amount });
        } else {
          totals.deductions += amount;
          deductionsList.push({ name: component.name, amount });
        }
      } else if (hasCategory(component, "benefit")) {
        if (isEmployer || nonPayroll) {
          totals.employerShares += amount;
          employerList.push({ name: component.name, amount });
        } else {
          totals.benefits += amount;
          benefitsList.push({ name: component.name, amount });
        }
      } else if (hasCategory(component, "reimbursement")) {
        if (nonPayroll) {
          totals.nonPayroll = roundAmount((totals.nonPayroll || 0) + amount);
        } else {
          totals.reimbursements += amount;
          reimbursementsList.push({ name: component.name, amount });
        }
      }
    });

    // Structure-option Fixed CTC (NPS / Meal) — reserved in waterfall; surface on CTC total
    const addonReserve = estimateFixedCtcAddonReserve(monthlyCtc);
    totals.addonFixedCtc = addonReserve;
    totals.gross = roundAmount(totals.earnings + totals.benefits + totals.reimbursements);
    totals.earningsSubtotal = roundAmount(totals.earnings + totals.benefits);
    const totalStatutoryDeductions = statutoryDeductionsList.reduce((sum, i) => sum + i.amount, 0);
    const totalStatutoryEmployer = statutoryEmployerList.reduce((sum, i) => sum + i.amount, 0);
    totals.deductions = roundAmount(totals.deductions + totalStatutoryDeductions);
    totals.employerShares = roundAmount(totals.employerShares + totalStatutoryEmployer);
    totals.net = roundAmount(totals.gross - totals.deductions);
    totals.monthlyCtc = monthlyCtc;
    totals.annualCtc = roundAmount(monthlyCtc * 12);
    totals.actualCtcMonthly = roundAmount(
      totals.gross + totals.employerShares + (totals.nonPayroll || 0) + addonReserve
    );
    totals.actualCtcAnnual = roundAmount(totals.actualCtcMonthly * 12);
    totals.grossAnnual = roundAmount(totals.gross * 12);
    totals.earningsSubtotalAnnual = roundAmount(totals.earningsSubtotal * 12);
    totals.reimbursementsAnnual = roundAmount(totals.reimbursements * 12);
    totals.netAnnual = roundAmount(totals.net * 12);
    totals.deductionsAnnual = roundAmount(totals.deductions * 12);
    totals.employerSharesAnnual = roundAmount(totals.employerShares * 12);

    const balanceAmount =
      balanceComponentId != null ? Number(amountsByComponentId[balanceComponentId] || 0) : null;
    const balanceComponent =
      balanceComponentId != null
        ? formData.components.find((c) => c.id === balanceComponentId)
        : null;
    // GROSS: Profile Gross = earnings Subtotal (E). CTC: Profile CTC = Total CTC (E+B[+R]).
    const isGrossMode = String(formData.salaryType || "ctc").toLowerCase() === "gross";
    const matchedMonthly = isGrossMode
      ? roundAmount(totals.earningsSubtotal ?? totals.earnings)
      : roundAmount(totals.actualCtcMonthly);
    const packageDelta = roundAmount(matchedMonthly - monthlyCtc);
    const hasNegativeBalance = balanceAmount != null && balanceAmount < 0;
    const hasCtcMismatch = Math.abs(packageDelta) > 1;

    return {
      amountsByComponentId,
      totals,
      earningsList,
      deductionsList,
      employerList,
      benefitsList,
      reimbursementsList,
      statutoryDeductionsList,
      statutoryEmployerList,
      pfWageBase: pfBase,
      balanceComponentId,
      balanceAmount,
      balanceComponentName: balanceComponent?.name || null,
      ctcDelta: packageDelta,
      packageDelta,
      isGrossMode,
      matchedMonthly,
      hasNegativeBalance,
      hasCtcMismatch,
    };
  };

  const calculateStructureTotals = () => {
    return formulaEngine.calculateStructureTotals(formData.components);
  };

  const nextStep = () => {
    if (formData.currentStep === 1) {
      if (!formData.name.trim()) {
        toast.error("Please enter a structure name");
        return;
      }
      if (!formData.salaryType) {
        toast.error("Please select a salary structure type");
        return;
      }
    }
    if (formData.currentStep === 2) {
      if (formData.components.length === 0) {
        toast.error("Please add at least one component");
        return;
      }
      // Skip Simulator unless Formula is used AND user opted in.
      const runSimulator = enableSimulator && hasFormulaComponent;
      setFormData((prev) => ({
        ...prev,
        currentStep: runSimulator ? 3 : 4,
      }));
      if (!runSimulator) setSimulatorValidated(false);
      return;
    }
    if (formData.currentStep === 3 && !simulatorValidated) {
      toast.error("Please validate the structure in the Simulator step before proceeding to Assign Structure.");
      return;
    }
    setFormData(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  };

  const prevStep = () => {
    if (formData.currentStep === 3) setSimulatorValidated(false);
    // If Simulator was skipped, Assign → Components
    if (formData.currentStep === 4 && !(enableSimulator && hasFormulaComponent)) {
      setFormData((prev) => ({ ...prev, currentStep: 2 }));
      return;
    }
    setFormData(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleReset = () => {
    setSimulatorValidated(false);
    setEnableSimulator(false);
    setSimulatorCtcAmount('');
    setSimulatorPbAmount('');
    setSimulatorPbPeriod('annual');
    setFormData({
      name: '',
      salaryType: 'ctc',
      components: [],
      currentStep: 1,
      effectiveMonth: '',
      performanceBonus: normalizePerformanceBonusConfig({ enabled: false, frequency: 'annual' }),
      nps: defaultNpsConfig(),
      vpf: defaultVpfConfig(),
      mealVoucher: defaultMealVoucherConfig(),
      assignment: {
        allEmployees: false,
        branch: '',
        department: '',
        designation: '',
        grade: '',
        category: '',
        subDepartment: '',
        location: '',
        employmentTypes: [],
        employeeIds: [],
      },
    });
  };

  const getCategoryLabels = (categories) => {
    const labels = [];
    if (categories.earning) labels.push('Earning');
    if (categories.deduction) labels.push('Deduction');
    if (categories.benefit) labels.push('Benefit');
    if (categories.reimbursement) labels.push('Reimbursement');
    return labels.join(', ');
  };

  const getCategoryBadgeColor = (categories) => {
    if (categories.earning) return 'bg-green-100 text-green-800';
    if (categories.deduction) return 'bg-red-100 text-red-800';
    if (categories.benefit) return 'bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))]';
    if (categories.reimbursement) return 'bg-[hsl(var(--ds-info)/0.12)] text-[hsl(var(--ds-info))]';
    return 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto bg-black/50 p-4">
      <div
        className="flex h-[min(90vh,900px)] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-[hsl(var(--ds-surface))] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="salary-structure-form-title"
      >
        {/* Modal Header - Fixed */}
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div className="min-w-0">
            <h2 id="salary-structure-form-title" className="text-lg font-semibold text-[hsl(var(--ds-fg))]">
              {editingStructure ? "Edit Salary Structure" : "Create New Salary Structure"}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              {[
                { step: 1, label: "Details" },
                { step: 2, label: "Components" },
                ...(enableSimulator && hasFormulaComponent
                  ? [{ step: 3, label: "Simulator" }]
                  : []),
                { step: 4, label: "Assign" },
              ].map(({ step, label }, index) => {
                const reached =
                  formData.currentStep === step ||
                  (formData.currentStep > step &&
                    !(step === 3 && formData.currentStep === 4 && !enableSimulator));
                // When Simulator is hidden, treat Assign as visually "step 3"
                const displayNo = index + 1;
                return (
                <div
                  key={step}
                  className={`flex items-center ${
                    reached ? "text-[hsl(var(--ds-secondary))]" : "text-[hsl(var(--ds-muted-fg))]"
                  }`}
                >
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                      reached
                        ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                        : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    {displayNo}
                </div>
                  <span className="ml-1.5 text-xs font-medium sm:text-sm">{label}</span>
              </div>
              );
              })}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {editingStructure && formData.components?.length > 0 ? (
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={exportingConfig}
                onClick={handleExportConfigHistory}
              >
                {exportingConfig ? "Exporting…" : "Export"}
              </DsButton>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-[hsl(var(--ds-muted-fg))] transition-colors hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-muted-fg))]"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body — scrollable content area with fixed header/footer */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            className={`min-h-0 flex-1 px-5 py-4 ${
              formData.currentStep === 1 || formData.currentStep === 2
                ? "flex flex-col overflow-hidden"
                : "overflow-x-hidden overflow-y-auto"
            }`}
          >
          {formData.currentStep === 1 && (
            <div className="flex min-h-0 flex-1 flex-col gap-4">
              {/* Structure Name */}
              <div className="shrink-0">
                <label htmlFor="structure_name" className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-2">
                  Structure Name *
                </label>
                <DsInput
                  type="text"
                  id="structure_name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter structure name"
                />
              </div>

              {/* Salary Type Toggle */}
              <div className="shrink-0">
                <label className="block text-sm font-medium text-[hsl(var(--ds-fg))] mb-3">
                  Type of Salary Structure *
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="salary_type_gross"
                      name="salaryType"
                      value="gross"
                      checked={formData.salaryType === 'gross'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-[hsl(var(--ds-secondary))] focus:ring-[hsl(var(--ds-ring))] border-[hsl(var(--ds-border))]"
                    />
                    <label htmlFor="salary_type_gross" className="ml-2 text-sm text-[hsl(var(--ds-fg))]">
                      Gross Salary
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="salary_type_ctc"
                      name="salaryType"
                      value="ctc"
                      checked={formData.salaryType === 'ctc'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-[hsl(var(--ds-secondary))] focus:ring-[hsl(var(--ds-ring))] border-[hsl(var(--ds-border))]"
                    />
                    <label htmlFor="salary_type_ctc" className="ml-2 text-sm text-[hsl(var(--ds-fg))]">
                      CTC (Cost to Company)
                    </label>
                  </div>
                </div>
                <div className="mt-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                  {formData.salaryType === 'gross' ? (
                    <span>Gross Salary: Take-home salary before deductions</span>
                  ) : (
                    <span>CTC: Total cost to company including all benefits and deductions</span>
                  )}
                </div>
              </div>

              {/* Structure options — 1×4 checkboxes; details + Effective from in popup */}
              <div className="shrink-0 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-3">
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                  {[
                    {
                      key: "performanceBonus",
                      label: "Performance Bonus",
                      info:
                        formData.salaryType === "gross"
                          ? "Total Gross includes Fixed + PB. Component breakdown uses Fixed only (Total − PB). Set payout frequency and Effective from in the popup. Payout % is asked when PB is paid."
                          : "Total CTC includes Fixed + PB. Component breakdown uses Fixed only (Total − PB). Set payout frequency and Effective from in the popup. Payout % is asked when PB is paid.",
                    },
                    {
                      key: "nps",
                      label: "Employer NPS",
                      info: "Employer NPS benefit (like PF Employer). TDS 80CCD(2). Counts toward ₹7.5L employer PF+NPS+SA perquisite cap. Rate mode, % / wage basis, and Effective from are set in the popup.",
                    },
                    {
                      key: "vpf",
                      label: "VPF",
                      info: "Voluntary Provident Fund — employee deduction under 80C (with Emp PF). PF-applicable employees only. Max 88% of PF wage. Rate mode and Effective from are set in the popup.",
                    },
                    {
                      key: "mealVoucher",
                      label: "Meal Voucher",
                      info: "Meal / food vouchers — tax-free up to ₹200 per meal (IT Rules 2026). Amount, meals/day, mode, and Effective from are set in the popup.",
                    },
                  ].map((item) => {
                    const cfg = formData[item.key] || {};
                    const enabled = !!cfg.enabled;
                    const effYm = String(cfg.effectiveFrom || "").slice(0, 7);
                    return (
                      <div
                        key={item.key}
                        className="relative flex min-w-0 items-center gap-1.5 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-2"
                      >
                        <label className="inline-flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-sm text-[hsl(var(--ds-fg))]">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded"
                            checked={enabled || addonModal === item.key}
                            onChange={(e) => {
                              const next = e.target.checked;
                              if (next) {
                                setAddonInfoKey(null);
                                setAddonModal(item.key);
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  [item.key]: { ...(prev[item.key] || {}), enabled: false },
                                }));
                              }
                            }}
                          />
                          <span
                            className="min-w-0 truncate font-medium"
                            title={
                              enabled
                                ? `${item.label}${effYm ? ` · from ${effYm}` : ""} — click to configure`
                                : item.label
                            }
                            onClick={(e) => {
                              if (!enabled) return;
                              e.preventDefault();
                              setAddonInfoKey(null);
                              setAddonModal(item.key);
                            }}
                          >
                            {item.label}
                          </span>
                        </label>
                        <button
                          type="button"
                          className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                            addonInfoKey === item.key
                              ? "bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]"
                              : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-primary))]"
                          }`}
                          aria-label={`About ${item.label}`}
                          aria-expanded={addonInfoKey === item.key}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAddonInfoKey((k) => (k === item.key ? null : item.key));
                          }}
                        >
                          <Info className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                        </button>
                        {addonInfoKey === item.key ? (
                          <div
                            className="absolute left-0 right-0 top-full z-20 mt-1 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2 text-[11px] leading-snug text-[hsl(var(--ds-muted-fg))] shadow-lg"
                            role="tooltip"
                          >
                            {item.info}
                            {enabled ? (
                              <button
                                type="button"
                                className="mt-1.5 block text-[11px] font-medium text-[hsl(var(--ds-primary))] hover:underline"
                                onClick={() => {
                                  setAddonInfoKey(null);
                                  setAddonModal(item.key);
                                }}
                              >
                                Configure details…
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Available Components — fills down to footer actions */}
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="mb-2 flex shrink-0 items-center justify-between">
                  <label className="block text-sm font-medium text-[hsl(var(--ds-fg))]">
                    Available Components ({savedComponents.length})
                  </label>
                  <div className="flex items-center gap-2">
                    {formData.components.length > 0 && (
                      <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
                        {formData.components.length} selected
                      </span>
                    )}
                    {savedComponents.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (formData.components.length === savedComponents.length) {
                            setFormData((prev) => ({ ...prev, components: [] }));
                          } else {
                            const all = [];
                            savedComponents.forEach((c) => {
                              all.push(buildStructureComponent(c, all));
                            });
                            setFormData((prev) => ({ ...prev, components: all }));
                          }
                        }}
                        className="rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-xs hover:bg-[hsl(var(--ds-muted))]"
                      >
                        {formData.components.length === savedComponents.length
                          ? "Deselect All"
                          : "Select All"}
                      </button>
                    )}
                  </div>
                </div>
                {loading ? (
                  <div className="py-4 text-center text-sm text-[hsl(var(--ds-muted-fg))]">Loading…</div>
                ) : (
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {savedComponents.length === 0 ? (
                      <p className="py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
                        No components available — create components first.
                      </p>
                    ) : (
                      <div>
                        {savedComponents.map((component) => {
                          const isAdded = isComponentSelected(component);
                          const cats = normalizeCategories(component);
                          const catLabel = cats.earning
                            ? "Earning"
                            : cats.deduction
                              ? "Deduction"
                              : cats.benefit
                                ? "Benefit"
                                : cats.reimbursement
                                  ? "Reimbursement"
                                  : "—";
                          return (
                            <div
                              key={component.id}
                              className="flex items-center gap-2 border-b border-[hsl(var(--ds-border))] px-1 py-2 text-sm last:border-b-0"
                            >
                              <span className="min-w-0 flex-1 truncate font-medium text-[hsl(var(--ds-fg))]">
                                      {component.name}
                                      </span>
                              <span className="shrink-0 text-xs text-[hsl(var(--ds-muted-fg))]">{catLabel}</span>
                              {component.sortOrder != null && Number(component.sortOrder) > 0 && (
                                <span className="w-8 shrink-0 text-right text-xs text-[hsl(var(--ds-muted-fg))]">
                                  #{component.sortOrder}
                                      </span>
                                    )}
                                <button
                                type="button"
                                onClick={() => {
                                  if (isAdded) handleComponentRemove(component.id);
                                  else handleComponentAdd(component.id);
                                }}
                                className={`shrink-0 rounded border px-2 py-0.5 text-xs ${
                                    isAdded
                                    ? "border-red-200 text-red-700 hover:bg-red-50"
                                    : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                                }`}
                              >
                                {isAdded ? "Remove" : "Add"}
                                </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {formData.currentStep === 2 && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="mb-4 shrink-0 border-b border-[hsl(var(--ds-border))] pb-3">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <div>
                    <h3 className="text-lg font-semibold text-[hsl(var(--ds-fg))]">Configure components</h3>
                  </div>
                  <span className="shrink-0 text-sm text-[hsl(var(--ds-muted-fg))]">
                    {formData.components.length} components
                  </span>
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-3 overflow-x-hidden overflow-y-auto">
                {formData.components.map((component) => {
                  const monthlyVar = isMonthlyVariableComponent(component);
                  const usePercent = isManualPercentComponent(component);
                  const basisDisabled = monthlyVar;
                  return (
                  <div
                    key={component.id}
                    className={`min-w-0 rounded-xl border bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)] ${
                      component.isActive === false
                        ? "border-dashed border-[hsl(var(--ds-border))] opacity-75"
                        : "border-[hsl(var(--ds-border))]"
                    }`}
                  >
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <h4 className="text-base font-semibold text-[hsl(var(--ds-fg))]">{component.name}</h4>
                        {component.categories?.earning && (
                          <span className="rounded-md bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-800">Earning</span>
                        )}
                        {component.categories?.deduction && (
                          <span className="rounded-md bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-800">Deduction</span>
                        )}
                        {component.categories?.benefit && (
                          <span className="rounded-md bg-[hsl(var(--ds-muted))] px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">Benefit</span>
                        )}
                        {component.categories?.reimbursement && (
                          <span className="rounded-md bg-[hsl(var(--ds-info)/0.12)] px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--ds-info))]">Reimbursement</span>
                        )}
                        {monthlyVar && (
                          <span className="rounded-md bg-[hsl(var(--ds-warning)/0.15)] px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--ds-warning))]">Monthly Variable</span>
                        )}
                        {!monthlyVar && (
                          <span className="rounded-md bg-[hsl(var(--ds-primary)/0.12)] px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--ds-primary))]">Fixed</span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {component.isActive === false && (
                          <button
                            type="button"
                            onClick={() => {
                              handleComponentSettingChange(component.id, "isActive", true);
                              handleComponentSettingChange(
                                component.id,
                                "statusEffectiveFrom",
                                new Date().toISOString().slice(0, 7)
                              );
                              toast.success(`${component.name} marked active — click Save to persist`);
                            }}
                            className="rounded border border-emerald-300 px-2 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-50"
                          >
                            Reactivate
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setHistoryModalComponentId(component.id)}
                          className="rounded border border-[hsl(var(--ds-border))] px-2 py-0.5 text-[11px] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                        >
                          History
                          {(() => {
                            const n = consolidateComponentHistory(component.configHistory).length;
                            return n ? ` (${n})` : "";
                          })()}
                        </button>
                        <DsButton
                          type="button"
                          variant="primary"
                          size="sm"
                          disabled={savingComponentId === component.id || !editingStructure?.id}
                          onClick={() => handleSaveComponentConfig(component.id)}
                          title={
                            editingStructure?.id
                              ? "Save this component to the database (creates one history version)"
                              : "Save the structure once first"
                          }
                        >
                          {savingComponentId === component.id ? "Saving…" : "Save"}
                        </DsButton>
                        <label className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--ds-muted-fg))] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={component.isActive !== false}
                            onChange={(e) =>
                              handleComponentSettingChange(component.id, "isActive", e.target.checked)
                            }
                            className="h-3.5 w-3.5 rounded"
                          />
                          Active
                        </label>
                        <button
                          type="button"
                          onClick={() => handleComponentRemove(component.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          aria-label="Remove component"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
                      <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Status from</span>
                      <input
                        type="month"
                        value={String(component.statusEffectiveFrom || "").slice(0, 7)}
                        min={statusFromMinMonth}
                        onChange={(e) =>
                          handleComponentSettingChange(
                            component.id,
                            "statusEffectiveFrom",
                            clampYm(e.target.value, statusFromMinMonth) || e.target.value
                          )
                        }
                        className="rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm"
                      />
                    </div>

                    {component.categories?.reimbursement ? (
                      <div className="space-y-3 text-sm">
                        <p className="rounded-lg border border-[hsl(var(--ds-info)/0.35)] bg-[hsl(var(--ds-info)/0.06)] px-3 py-2 text-[11px] leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                          Reimbursement policy (Method A/B, tax, declare/claim, PF/ESI) comes from the
                          <strong> component library</strong> and company Reimbursement settings.
                          Here set only effective month, order, and the monthly CTC ceiling so Special
                          Allowance (Balance CTC) can absorb the leftover. Opt-in does not rewrite this
                          structure.
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="w-28 shrink-0 text-[hsl(var(--ds-muted-fg))]">Order No.</span>
                          <input
                            type="number"
                            min={2}
                            step={1}
                            value={component.orderNo ?? ""}
                            onChange={(e) =>
                              handleComponentSettingChange(component.id, "orderNo", e.target.value)
                            }
                            className="w-20 rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm"
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="w-28 shrink-0 text-[hsl(var(--ds-muted-fg))]">
                            CTC ceiling (₹ / month)
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={component.manualAmount ?? ""}
                            onChange={(e) => {
                              handleComponentSettingChange(component.id, "calculationType", "manual");
                              handleComponentSettingChange(component.id, "calculationMethod", METHOD_FIXED);
                              handleComponentSettingChange(component.id, "manualAmountType", "amount");
                              handleComponentSettingChange(component.id, "manualAmount", e.target.value);
                              handleComponentSettingChange(component.id, "autoAdjustCTC", false);
                              handleComponentSettingChange(component.id, "componentNature", "payroll");
                              handleComponentSettingChange(component.id, "taxTreatment", "partial");
                              handleComponentSettingChange(component.id, "pfApplicable", false);
                              handleComponentSettingChange(component.id, "esiApplicable", false);
                              handleComponentSettingChange(component.id, "gratuityApplicable", false);
                            }}
                            placeholder="Monthly budget in CTC"
                            className="w-44 rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-sm"
                          />
                        </div>
                      </div>
                    ) : (
                    /* Row 1–2 | Row 3–4 — plain 2-column grid */
                    <div className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
                      {/* Row 1 left: Order */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Order No.</span>
                        <input
                          type="number"
                          min={isBasicLike(component) ? 1 : 2}
                          step={1}
                          value={component.orderNo ?? (isBasicLike(component) ? 1 : "")}
                          disabled={isBasicLike(component)}
                          onChange={(e) =>
                            handleComponentSettingChange(component.id, "orderNo", e.target.value)
                          }
                          className="w-20 px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm disabled:bg-[hsl(var(--ds-muted))]"
                        />
                      </div>

                      {/* Nature: Payroll vs Non-payroll */}
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Nature</span>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`nature_${component.id}`}
                            checked={!isNonPayrollComponent(component)}
                            onChange={() =>
                              handleComponentSettingChange(component.id, "componentNature", "payroll")
                            }
                            className="h-3.5 w-3.5"
                          />
                          Payroll
                        </label>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`nature_${component.id}`}
                            checked={isNonPayrollComponent(component)}
                            onChange={() =>
                              handleComponentSettingChange(component.id, "componentNature", "nonPayroll")
                            }
                            className="h-3.5 w-3.5"
                          />
                          Non-payroll
                        </label>
                      </div>

                      {/* Row 1 right: Calc type + balance */}
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Calc Type</span>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`calc_type_${component.id}`}
                            checked={component.calculationType === "manual"}
                            onChange={() =>
                              handleComponentSettingChange(component.id, "calculationType", "manual")
                            }
                            className="h-3.5 w-3.5"
                          />
                          Manual
                        </label>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`calc_type_${component.id}`}
                            checked={component.calculationType === "formula"}
                            onChange={() =>
                              handleComponentSettingChange(component.id, "calculationType", "formula")
                            }
                            className="h-3.5 w-3.5"
                          />
                          Formula
                        </label>
                        {formData.salaryType === "ctc" &&
                          component.categories?.earning &&
                          !isBasicLike(component) &&
                          !monthlyVar && (
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!component.autoAdjustCTC}
                                onChange={(e) =>
                                  handleComponentSettingChange(
                                    component.id,
                                    "autoAdjustCTC",
                                    e.target.checked
                                  )
                                }
                                className="h-3.5 w-3.5 rounded"
                              />
                              Balance CTC
                            </label>
                          )}
                        </div>
                        
                      {/* Formula — one full-width line */}
                      {component.calculationType === "formula" && (
                        <div className="md:col-span-2 flex flex-wrap items-center gap-2">
                          <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Formula</span>
                          <input
                            type="text"
                            readOnly
                            value={component.selectedFormula?.formula || ""}
                            placeholder="No formula selected"
                            className="min-w-0 flex-1 px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm font-mono bg-[hsl(var(--ds-muted))]"
                          />
                              <button
                                type="button"
                                onClick={() => openFormulaManager(component.id)}
                            className="px-2 py-1 text-xs border border-[hsl(var(--ds-border))] rounded hover:bg-[hsl(var(--ds-muted))]"
                              >
                            {component.selectedFormula ? "Change" : "Select"}
                              </button>
                            </div>
                      )}

                      {component.calculationType === "manual" && !monthlyVar && (
                        <div className="md:col-span-2 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Value</span>
                          <label className="inline-flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`amt_type_${component.id}`}
                              checked={!usePercent}
                              onChange={() =>
                                handleComponentSettingChange(component.id, "manualAmountType", "amount")
                              }
                              className="h-3.5 w-3.5"
                            />
                            Amount (₹ / month)
                          </label>
                          <label className="inline-flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`amt_type_${component.id}`}
                              checked={usePercent}
                              onChange={() =>
                                handleComponentSettingChange(component.id, "manualAmountType", "percent")
                              }
                              className="h-3.5 w-3.5"
                            />
                            % of Fixed CTC / Basic / Gross
                          </label>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 md:pl-28">
                          {usePercent ? (
                            <>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={component.manualPercent ?? ""}
                                onChange={(e) =>
                                  handleComponentSettingChange(
                                    component.id,
                                    "manualPercent",
                                    e.target.value
                                  )
                                }
                                placeholder="%"
                                className="w-24 px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm"
                              />
                              <select
                                value={component.percentBasis || "CTC"}
                                onChange={(e) =>
                                  handleComponentSettingChange(
                                    component.id,
                                    "percentBasis",
                                    e.target.value
                                  )
                                }
                                className="px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm"
                              >
                                <option value="CTC">Fixed CTC</option>
                                <option value="BASIC">Basic</option>
                                <option value="GROSS">Gross</option>
                              </select>
                            </>
                          ) : (
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={component.manualAmount ?? ""}
                              onChange={(e) =>
                                handleComponentSettingChange(
                                  component.id,
                                  "manualAmount",
                                  e.target.value
                                )
                              }
                              placeholder="₹ / month"
                              className="w-40 px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm"
                            />
                          )}
                          </div>
                        </div>
                      )}

                      {component.calculationType === "manual" && monthlyVar && (
                        <div className="md:col-span-2 text-xs text-[hsl(var(--ds-muted-fg))]">
                          Amount is entered in Run Payroll → Monthly Variables (formula optional under Calc Type).
                        </div>
                      )}

                      {/* Method */}
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Method</span>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`calc_method_${component.id}`}
                            checked={!monthlyVar}
                            disabled={isBasicLike(component)}
                            onChange={() =>
                              handleComponentSettingChange(
                                component.id,
                                "calculationMethod",
                                METHOD_FIXED
                              )
                            }
                            className="h-3.5 w-3.5 disabled:opacity-40"
                          />
                          Fixed
                        </label>
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name={`calc_method_${component.id}`}
                            checked={monthlyVar}
                            disabled={isBasicLike(component)}
                            onChange={() =>
                              handleComponentSettingChange(
                                component.id,
                                "calculationMethod",
                                METHOD_MONTHLY_VARIABLE
                              )
                            }
                            className="h-3.5 w-3.5 disabled:opacity-40"
                          />
                          Monthly Variable
                        </label>
                      </div>

                      {/* Basis */}
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Basis</span>
                        {[
                          { v: "payDays", l: "Pay Days" },
                          { v: "presentDays", l: "Present Days" },
                        ].map(({ v, l }) => (
                          <label
                            key={v}
                            className={`inline-flex items-center gap-1 ${
                              basisDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`calc_basis_${component.id}`}
                              value={v}
                              checked={
                                !basisDisabled &&
                                (component.calculationBasis === v ||
                                  (!component.calculationBasis && v === "payDays"))
                              }
                              onChange={(e) =>
                                handleComponentSettingChange(
                                  component.id,
                                  "calculationBasis",
                                  e.target.value
                                )
                              }
                              disabled={basisDisabled}
                              className="h-3.5 w-3.5 disabled:opacity-40"
                            />
                            {l}
                          </label>
                        ))}
                        {basisDisabled && (
                          <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
                            N/A for Monthly Variable
                          </span>
                        )}
                      </div>

                      {/* Row 4 left: Tax */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Tax</span>
                        {component.categories?.deduction ? (
                          <select
                            value={component.taxTreatment || ""}
                            onChange={(e) =>
                              handleComponentSettingChange(
                                component.id,
                                "taxTreatment",
                                e.target.value
                              )
                            }
                            required
                            className="flex-1 min-w-[10rem] px-2 py-1 border border-[hsl(var(--ds-border))] rounded text-sm"
                          >
                            <option value="">Select…</option>
                            <option value="deduction_head">Deduction Head</option>
                            <option value="section10">Section 10</option>
                            <option value="section16">Section 16</option>
                            <option value="section24">Section 24</option>
                            <option value="section80C">Section 80C</option>
                            <option value="section80CCD">Section 80CCD</option>
                            <option value="section80CCD1">Section 80CCD(1)</option>
                            <option value="section80CCD1B">Section 80CCD(1B)</option>
                            <option value="section80CCD2">Section 80CCD(2)</option>
                            <option value="section80D">Section 80D</option>
                            <option value="section80E">Section 80E</option>
                            <option value="section80G">Section 80G</option>
                            <option value="section80TTA">Section 80TTA</option>
                            <option value="section80TTB">Section 80TTB</option>
                            <option value="section87A">Section 87A</option>
                            <option value="nontaxable">Non-Taxable</option>
                          </select>
                        ) : (
                          <div className="flex flex-wrap gap-3">
                            {[
                              { v: "taxable", l: "Taxable" },
                              { v: "nontaxable", l: "Non-Taxable" },
                              { v: "onetime", l: "One Time" },
                              { v: "partial", l: "Partial" },
                            ].map(({ v, l }) => (
                              <label key={v} className="inline-flex items-center gap-1 cursor-pointer">
                              <input
                                type="radio"
                                name={`tax_${component.id}`}
                                  value={v}
                                  checked={component.taxTreatment === v}
                                  onChange={(e) =>
                                    handleComponentSettingChange(
                                      component.id,
                                      "taxTreatment",
                                      e.target.value
                                    )
                                  }
                                  className="h-3.5 w-3.5"
                                />
                                {l}
                              </label>
                            ))}
                          </div>
                        )}
                    </div>

                      {/* Row 4 right: Applicable */}
                      {!component.categories?.deduction ? (
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-[hsl(var(--ds-muted-fg))] w-28 shrink-0">Applicable</span>
                          {[
                            { k: "pfApplicable", l: "PF" },
                            { k: "esiApplicable", l: "ESI" },
                            { k: "gratuityApplicable", l: "Gratuity" },
                          ].map(({ k, l }) => (
                            <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                                checked={!!component[k]}
                                onChange={(e) =>
                                  handleComponentSettingChange(component.id, k, e.target.checked)
                                }
                                className="h-3.5 w-3.5 rounded"
                              />
                              {l}
                          </label>
                          ))}
                        </div>
                      ) : (
                        <div />
                      )}
                    </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {formData.currentStep === 3 && (() => {
            const {
              totals,
              earningsList,
              deductionsList,
              employerList,
              benefitsList,
              reimbursementsList,
              statutoryDeductionsList,
              statutoryEmployerList,
              balanceAmount,
              balanceComponentName,
              hasNegativeBalance,
            } = getSimulatorTotals();
            const hasComponentResults = formData.components.length > 0;
            return (
            <div className="flex min-h-0 flex-1 flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">Salary Structure Simulator</h3>
                <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                  Basic → Order No. waterfall · Min CTC = MW + employer on Basic
                </p>
              </div>

              <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 text-sm">
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-2">
                    <span className="w-28 shrink-0 text-[hsl(var(--ds-muted-fg))]">
                      Total {formData.salaryType === "gross" ? "Gross" : "CTC"}
                    </span>
                    <label className="inline-flex items-center gap-1">
                        <input
                          type="radio"
                          name="simulatorCtcType"
                        checked={simulatorCtcType === "monthly"}
                        onChange={() => setSimulatorCtcType("monthly")}
                        className="h-3.5 w-3.5"
                      />
                      Monthly
                      </label>
                    <label className="inline-flex items-center gap-1">
                        <input
                          type="radio"
                          name="simulatorCtcType"
                        checked={simulatorCtcType === "annual"}
                        onChange={() => setSimulatorCtcType("annual")}
                        className="h-3.5 w-3.5"
                      />
                      Annual
                      </label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={simulatorCtcAmount}
                      onChange={(e) => {
                        setSimulatorCtcAmount(e.target.value);
                        setSimulatorValidated(false);
                      }}
                      placeholder={simulatorCtcType === "monthly" ? "e.g. 50000" : "e.g. 1800000"}
                      className="min-w-[8rem] flex-1 max-w-xs rounded border border-[hsl(var(--ds-border))] px-2 py-1.5"
                    />
                  </div>

                  {formData.performanceBonus?.enabled && (
                    <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30 px-3 py-2">
                      <span className="w-28 shrink-0 text-[hsl(var(--ds-muted-fg))]">PB amount</span>
                      <label className="inline-flex items-center gap-1">
                        <input
                          type="radio"
                          name="simulatorPbPeriod"
                          checked={simulatorPbPeriod === "monthly"}
                          onChange={() => setSimulatorPbPeriod("monthly")}
                          className="h-3.5 w-3.5"
                        />
                        Monthly
                      </label>
                      <label className="inline-flex items-center gap-1">
                        <input
                          type="radio"
                          name="simulatorPbPeriod"
                          checked={simulatorPbPeriod === "annual"}
                          onChange={() => setSimulatorPbPeriod("annual")}
                          className="h-3.5 w-3.5"
                        />
                        Annual
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="100"
                        value={simulatorPbAmount}
                        onChange={(e) => {
                          setSimulatorPbAmount(e.target.value);
                          setSimulatorValidated(false);
                        }}
                        placeholder={simulatorPbPeriod === "monthly" ? "e.g. 25000" : "e.g. 300000"}
                        className="min-w-[8rem] flex-1 max-w-xs rounded border border-[hsl(var(--ds-border))] px-2 py-1.5"
                      />
                      <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
                        Freq: {PB_FREQUENCIES.find((f) => f.value === formData.performanceBonus?.frequency)?.label || "Annual"}
                        {" · "}
                        Fixed for breakdown = Total − PB
                      </span>
                    </div>
                  )}

                  {followMinimumWages && (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="w-20 shrink-0 text-[hsl(var(--ds-muted-fg))]">MW Cat.</span>
                        <select
                          value={simulatorCategoryId}
                          onChange={(e) => setSimulatorCategoryId(e.target.value)}
                          className="min-w-[8rem] flex-1 rounded border border-[hsl(var(--ds-border))] px-2 py-1.5"
                        >
                          <option value="">Select…</option>
                          {simulatorCategories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                  </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="w-20 shrink-0 text-[hsl(var(--ds-muted-fg))]">MW Month</span>
                    <input
                          type="month"
                          value={simulatorMwMonth}
                          onChange={(e) => setSimulatorMwMonth(e.target.value)}
                          className="min-w-[8rem] flex-1 rounded border border-[hsl(var(--ds-border))] px-2 py-1.5"
                        />
                  </div>
                      {simulatorMwAmount != null && (
                        <p className="sm:col-span-2 lg:col-span-3 text-xs text-[hsl(var(--ds-muted-fg))]">
                          MW ₹{Number(simulatorMwAmount).toLocaleString("en-IN")} applied as Basic floor
                        </p>
                      )}
                    </>
                  )}

                  {liveStatutoryRates?.pt?.states &&
                    Object.keys(liveStatutoryRates.pt.states).length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-1">
                      <span className="w-20 shrink-0 text-[hsl(var(--ds-muted-fg))]">PT (override)</span>
                      <select
                        value={simulatorPtState}
                        onChange={(e) => setSimulatorPtState(e.target.value)}
                        className="min-w-[8rem] flex-1 rounded border border-[hsl(var(--ds-border))] px-2 py-1.5"
                      >
                        {Object.keys(liveStatutoryRates.pt.states).map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 sm:col-span-2 lg:col-span-3">
                    <span className="w-20 shrink-0 text-[hsl(var(--ds-muted-fg))]">Statutory</span>
                    {[
                      { k: "pfApplicable", l: "PF" },
                      { k: "esiApplicable", l: "ESI" },
                      { k: "ptApplicable", l: "PT" },
                      { k: "gratuityApplicable", l: "Gratuity" },
                      { k: "ceilingPf", l: "Ceiling PF" },
                    ].map(({ k, l }) => (
                      <label key={k} className="inline-flex cursor-pointer items-center gap-1">
                        <input
                          type="checkbox"
                          checked={!!simulatorApplicability[k]}
                          onChange={(e) => {
                            setSimulatorApplicability((prev) => ({
                              ...prev,
                              [k]: e.target.checked,
                            }));
                            setSimulatorValidated(false);
                          }}
                          className="h-3.5 w-3.5 rounded"
                        />
                        {l}
                      </label>
                    ))}
                    </div>
                  </div>

                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[hsl(var(--ds-border))] pt-4">
                  {!simulatorValidated ? (
                    <DsButton
                        type="button"
                      variant="primary"
                        onClick={() => {
                        const result = getSimulatorTotals();
                        const { totals: t, hasNegativeBalance: negBal, hasCtcMismatch: ctcMiss, balanceComponentName: balName, balanceAmount: balAmt, ctcDelta: delta } = result;
                        if (negBal) {
                          toast.error(
                            `CTC is too low for fixed component amounts and minimum wages. ${balName || "Balance CTC component"} is short by ₹${Math.abs(balAmt).toLocaleString("en-IN")}/mo. Increase CTC or reduce fixed/manual amounts — only components marked "Balance CTC" can absorb the difference.`
                          );
                            return;
                          }
                        if (ctcMiss) {
                          const isGross = String(formData.salaryType || "").toLowerCase() === "gross";
                          toast.error(
                            isGross
                              ? `Subtotal (E) / Gross Salary is ₹${result.matchedMonthly?.toLocaleString("en-IN")}/mo but Profile Gross is ₹${t.monthlyCtc?.toLocaleString("en-IN")}/mo (difference ₹${Math.abs(delta).toLocaleString("en-IN")}). Adjust earnings or mark a Balance CTC component to auto-fit Gross.`
                              : `Total CTC (E+R+B) is ₹${t.actualCtcMonthly?.toLocaleString("en-IN")}/mo but Profile CTC is ₹${t.monthlyCtc?.toLocaleString("en-IN")}/mo (difference ₹${Math.abs(delta).toLocaleString("en-IN")}). Mark one earning component as "Balance CTC" to auto-fit.`
                          );
                          return;
                        }
                            if (t.net < 0) {
                          toast.error("Net pay is negative. Please adjust components or applicability.");
                              return;
                          }
                          setSimulatorValidated(true);
                        }}
                      >
                        Validate Structure
                    </DsButton>
                  ) : (
                    <p className="text-sm font-medium text-green-700">
                      Validated — you can proceed to Assign.
                    </p>
                  )}

                  {hasNegativeBalance && (
                    <div className="flex-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 min-w-[12rem]">
                      <p>
                        {balanceComponentName || "Balance CTC component"} is{" "}
                        <strong>₹{Math.abs(balanceAmount).toLocaleString("en-IN")}/mo negative</strong>.
                        Fixed components keep their configured amounts — increase CTC or adjust settings.
                      </p>
                        </div>
                  )}
                      </div>
                        </div>

              <div>
                <h4 className="mb-3 text-sm font-semibold text-[hsl(var(--ds-fg))]">Calculation Results</h4>
                {simulatorCtcAmount && hasComponentResults ? (
                  <SalaryStructureCalculationResults
                    earningsList={earningsList}
                    benefitsList={benefitsList}
                    reimbursementsList={reimbursementsList}
                    deductionsList={deductionsList}
                    employerList={employerList}
                    statutoryDeductionsList={statutoryDeductionsList}
                    statutoryEmployerList={statutoryEmployerList}
                    totals={totals}
                    packageMode={formData.salaryType === "gross" ? "GROSS" : "CTC"}
                  />
                ) : (
                  <p className="rounded-lg border border-dashed border-[hsl(var(--ds-border))] py-10 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
                    Enter {formData.salaryType === "gross" ? "Gross" : "CTC"} to preview calculation results.
                  </p>
                  )}
                </div>
            </div>
            );
          })()}

          {formData.currentStep === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-[hsl(var(--ds-fg))]">Assign Structure</h3>
              <SalaryStructureApplicabilityStep
                assignment={formData.assignment}
                onAssignmentChange={(assignment) => setFormData((prev) => ({ ...prev, assignment }))}
                masterLists={{
                  branchesList: resolvedOrgLevels.branches || [],
                  departmentsList: resolvedOrgLevels.departments || [],
                  designationsList: resolvedOrgLevels.designations || [],
                  gradesList: resolvedOrgLevels.grades || [],
                  categoriesList: resolvedOrgLevels.categories || [],
                  subDepartmentsList: resolvedOrgLevels.subDepartments || [],
                }}
              />
                        </div>
                      )}

                          </div>
                          
          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-5 py-3">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <DsButton type="button" variant="outline" onClick={handleReset}>
                Reset
              </DsButton>
              {formData.currentStep === 2 ? (
                <label
                  className={`inline-flex max-w-full cursor-pointer items-center gap-2 text-xs sm:text-sm ${
                    hasFormulaComponent
                      ? "text-[hsl(var(--ds-fg))]"
                      : "cursor-not-allowed text-[hsl(var(--ds-muted-fg))]"
                  }`}
                  title={
                    hasFormulaComponent
                      ? "Validate formula-based components before Assign"
                      : "Available when at least one component uses Formula"
                  }
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-[hsl(var(--ds-border))]"
                    checked={enableSimulator && hasFormulaComponent}
                    disabled={!hasFormulaComponent}
                    onChange={(e) => setEnableSimulator(e.target.checked)}
                  />
                  <span className="leading-snug">
                    Run salary simulator
                    {!hasFormulaComponent ? (
                      <span className="text-[hsl(var(--ds-muted-fg))]">
                        {" "}
                        (needs Formula)
                      </span>
                    ) : null}
                  </span>
                </label>
              ) : null}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <DsButton type="button" variant="outline" onClick={onClose}>
                Cancel
              </DsButton>

              {formData.currentStep > 1 && (
                <DsButton type="button" variant="outline" onClick={prevStep}>
                  Previous
                </DsButton>
              )}
              
              {formData.currentStep < 4 ? (
                <DsButton type="button" variant="primary" onClick={nextStep}>
                  Next
                </DsButton>
              ) : (
                <DsButton
                  type="button"
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!!loading}
                  loading={!!loading}
                >
                  {loading ? "Saving…" : "Save Structure"}
                </DsButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Formula Manager Modal */}
      <AdvancedFormulaManager
        isOpen={showFormulaManager}
        onClose={() => {
          setShowFormulaManager(false);
          setCurrentComponentForFormula(null);
        }}
        savedComponents={savedComponents}
        structures={structures}
        organizationalLevels={resolvedOrgLevels}
        statutoryRates={statutoryRates}
        onFormulaSelect={handleFormulaSelect}
        selectedFormula={currentComponentForFormula ? 
          formData.components.find(comp => comp.id === currentComponentForFormula)?.selectedFormula : 
          null
        }
      />

      <StructureAddonConfigModal
        isOpen={!!addonModal}
        onClose={() => setAddonModal(null)}
        title={
          addonModal === "performanceBonus"
            ? "Configure Performance Bonus"
            : addonModal === "nps"
              ? "Configure Employer NPS"
              : addonModal === "vpf"
                ? "Configure VPF"
                : addonModal === "mealVoucher"
                  ? "Configure Meal Voucher"
                  : "Configure"
        }
        addonKey={addonModal || "nps"}
        level="structure"
        minEffectiveMonth={statusFromMinMonth || undefined}
        infoText={
          addonModal === "performanceBonus"
            ? formData.salaryType === "gross"
              ? "Total Gross includes Fixed + PB. Component breakdown uses Fixed only (Total − PB). Accrual is attendance-prorated monthly; payout % applies when PB is paid via Monthly Variables (taxable under BONUS_INCENTIVE)."
              : "Total CTC includes Fixed + PB. Component breakdown uses Fixed only (Total − PB). Accrual is attendance-prorated monthly; payout % applies when PB is paid via Monthly Variables (taxable under BONUS_INCENTIVE)."
            : addonModal === "nps"
              ? "Employer NPS — Non-payroll, Fixed CTC, attendance-prorated. Out of Gross. TDS 80CCD(2). Counts toward ₹7.5L employer PF+NPS+SA perquisite cap."
              : addonModal === "vpf"
                ? "VPF — Non-payroll deduction (not CTC), nested under 80C with Emp PF. PF-applicable employees only. Max 88% of PF wage."
                : addonModal === "mealVoucher"
                  ? "Meal voucher — Non-payroll, Fixed CTC, attendance-prorated, out of Gross. Tax via MEAL_VOUCHER head (partially exempt up to ₹200/meal)."
                  : null
        }
        initialConfig={
          addonModal === "performanceBonus"
            ? formData.performanceBonus
            : addonModal
              ? formData[addonModal]
              : {}
        }
        onSave={(payload) => {
          if (!addonModal) return;
          if (addonModal === "performanceBonus") {
            setFormData((prev) => ({
              ...prev,
              performanceBonus: normalizePerformanceBonusConfig({
                ...payload,
                enabled: true,
                frequency: payload.frequency || "annual",
              }),
            }));
            return;
          }
          const normalize =
            addonModal === "nps"
              ? normalizeNpsConfig
              : addonModal === "vpf"
                ? normalizeVpfConfig
                : normalizeMealVoucherConfig;
          setFormData((prev) => ({
            ...prev,
            [addonModal]: normalize({ ...payload, enabled: true }),
          }));
        }}
      />

      <ComponentConfigHistoryModal
        open={historyModalComponentId != null}
        component={
          formData.components.find(
            (c) => String(c.id) === String(historyModalComponentId)
          ) || null
        }
        busy={historyBusy}
        onClose={() => setHistoryModalComponentId(null)}
        onRestore={(historyId) =>
          handleRestoreComponentHistory(historyModalComponentId, historyId)
        }
        onDelete={(historyId) =>
          handleDeleteComponentHistory(historyModalComponentId, historyId)
        }
      />
    </div>
  );
};

export default SalaryStructureForm;